/**
 * useStreamLifecycle — all streaming lifecycle effects for PrimateVisionV2.
 * Wires streamSession, pageState, isStreaming, resultPills, and related state
 * into a coherent session lifecycle. Effects-only hook — returns nothing.
 * Extracted from PrimateVisionV2.tsx (PRI-291 Phase 5)
 */
import { useEffect, useRef } from 'react'
import type { StreamSessionState, StreamSessionActions } from '../../core/hooks/types'
import type { Dispatch, RefObject, SetStateAction } from 'react'
import type { ResultPill } from '../../components/v2/pv2-shared'
import { stripCompiledQueryEnvelope } from '../../core/components/v2/pv2-shared'
import type { NarrativeEntry } from '../../lib/narrative'
import type { LiveInferenceResult } from '../../core/hooks/types'
import { createUserVideo, uploadStreamRecording, saveVideoSessionEntry, getStreamRecordingUrl } from '../../lib/api'
import { trackNarrativeSessionSummary, trackNarrativeGenerated, trackNarrativeFallback, trackStreamRecordingSaved } from '../../lib/analytics'

export interface UseStreamLifecycleParams {
  // From stream context (StreamContextValue)
  streamSession: StreamSessionState & StreamSessionActions
  isStreaming: boolean
  setIsStreaming: (v: boolean) => void
  resultPills: import('../../core/components/v2/pv2-shared').ResultPill[]
  setResultPills: import('react').Dispatch<import('react').SetStateAction<import('../../core/components/v2/pv2-shared').ResultPill[]>>
  activePrompt: string
  setFrozenFrameUrl: (v: string | null) => void
  frozenFrameUrl: string | null
  // From inference context
  pageState: string
  displayedResult: Record<string, unknown> | null
  jobId: string | null
  displayedJobId: string | null
  // From video library context
  demoVideos: { id: string; display_name: string; [k: string]: unknown }[]
  selectedSavedVideo: { id: string; [k: string]: unknown } | null
  selectedDemoVideo: string | null
  activeVideoId: string | null
  // From auth
  isSignedIn: boolean
  isPro: boolean
  // Refs declared in parent component
  streamPillIdRef: RefObject<string | null>
  lastStoppedPillIdRef: RefObject<string | null>
  uploadingPillIdsRef: RefObject<Set<string>>
  streamHistoryRef: RefObject<{ frame_num: number; answer: string; confidence: number; elapsed_s?: number; term_confidences?: Record<string, number>; recording_elapsed_ms?: number }[]>
  narrativeRef: RefObject<NarrativeEntry[]>
  lastStreamResultRef: RefObject<LiveInferenceResult | null>
  // Mutable ref values (updated by parent)
  isSignedInRef: RefObject<boolean>
  activePromptRef: RefObject<string>
  autoRetryCountRef: RefObject<number>
  autoRetryTimerRef: RefObject<ReturnType<typeof setTimeout> | null>
  streamWasLiveRef: RefObject<boolean>
  submittedThumbRef: RefObject<string | null>
  videoQueueRef: RefObject<{ id: string; userVideoId?: string; [k: string]: unknown }[]>
  // State not in contexts
  setStreamErrorState: (v: 'idle' | 'error' | 'retrying') => void
  setStreamProgressDismissing: (v: boolean) => void
  setOfflineProgressDismissing: (v: boolean) => void
  offlineJobActive: boolean
  setOfflineJobActive: (v: boolean) => void
  recordingLimitBannerShown: boolean
  setRecordingLimitBannerShown: Dispatch<SetStateAction<boolean>>
  setQuotaRefreshKey: Dispatch<SetStateAction<number>>
  setSlotRevealed: (v: boolean) => void
  setPrompt: (v: string) => void
  demoVideoUserVideoIds: Record<string, string>
  setDemoVideoUserVideoIds: Dispatch<SetStateAction<Record<string, string>>>
  // State setters for BEFORE/AFTER state machine
  setAfterState: (v: boolean) => void
  setBeforeStateActive: (v: boolean) => void
  // Library state setter
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setSavedUserVideos: (updater: any) => void
  // Callbacks from other hooks
  vibrate: (pattern: number | number[]) => void
  attachStreamRecordingUrl: (pillId: string, recordingUrl: string) => Promise<void>
  attachRenderedVideo: (pillId: string) => void
}

export function useStreamLifecycle({
  streamPillIdRef, lastStoppedPillIdRef, uploadingPillIdsRef, streamHistoryRef, narrativeRef,
  lastStreamResultRef, isSignedInRef, activePromptRef,
  autoRetryCountRef, autoRetryTimerRef, streamWasLiveRef, submittedThumbRef, videoQueueRef,
  setStreamErrorState, setStreamProgressDismissing, setOfflineProgressDismissing,
  offlineJobActive, setOfflineJobActive, recordingLimitBannerShown, setRecordingLimitBannerShown,
  setQuotaRefreshKey, setSlotRevealed, setPrompt, setSavedUserVideos, setAfterState, setBeforeStateActive,
  demoVideoUserVideoIds, setDemoVideoUserVideoIds,
  streamSession, isStreaming, setIsStreaming, resultPills, setResultPills,
  activePrompt, setFrozenFrameUrl, frozenFrameUrl,
  pageState, displayedResult, jobId, displayedJobId,
  demoVideos, selectedSavedVideo, selectedDemoVideo, activeVideoId,
  isSignedIn, isPro,
  vibrate, attachStreamRecordingUrl, attachRenderedVideo,
}: UseStreamLifecycleParams): void {

  // Internal refs (only needed within this hook)
  const prevIsStreamingRef = useRef(false)
  const prevPageStateRef = useRef(pageState)
  const wentThroughBufferingRef = useRef(false)

  // ── Stream lifecycle: freeze last frame when stream stops ─────────────────────
  useEffect(() => {
    const cs = streamSession.connectionState;
    // Track when stream was actually live so we know if a retry is worthwhile
    if (cs === 'live' || cs === 'degraded') {
      streamWasLiveRef.current = true;
    }
    // On error, camera issue, queue_full: reset isStreaming so UI recovers cleanly
    if (cs === 'error' || cs === 'camera_denied' || cs === 'no_camera' || cs === 'queue_full') {
      setIsStreaming(false);
      if (cs === 'queue_full') {
        // queue_full is never retryable immediately — show the error tray so the user
        // knows all slots are busy. Previously this left streamErrorState at 'idle'
        // which meant no StreamErrorTray ever appeared (DB-3).
        setStreamErrorState('error');
        return;
      }
      if (cs === 'error') {
        const errMsg = streamSession.errorMessage ?? '';
        // PRI-414: stamp the terminal error onto the active stream pill BEFORE
        // streamPillIdRef is nulled (the clear-on-stop effect below). Without
        // this, the pill's errorMessage prop went null on error and the user
        // saw a black RE-RUN STREAM card with a generic fallback — the actual
        // reason (slot_busy / prompt_timeout / terms…) was lost.
        if (errMsg && streamPillIdRef.current) {
          const errPillId = streamPillIdRef.current;
          setResultPills(prev => prev.map(p => p.id === errPillId ? { ...p, streamErrorMessage: errMsg } : p));
        }
        const isAuthError = errMsg.toLowerCase().includes('unauthorized') || errMsg.toLowerCase().includes('pending approval') || errMsg.toLowerCase().includes('access_restricted');
        // Auto-retry transient errors up to 2 times if we were already live
        if (!isAuthError && streamWasLiveRef.current && autoRetryCountRef.current < 2) {
          autoRetryCountRef.current += 1;
          setStreamErrorState('retrying');
          if (autoRetryTimerRef.current) clearTimeout(autoRetryTimerRef.current);
          autoRetryTimerRef.current = setTimeout(() => {
            autoRetryTimerRef.current = null;
            setStreamErrorState('idle');
            streamSession.reset();
            setIsStreaming(true);
            void streamSession.requestCameraAccess();
          }, 3000);
        } else {
          setStreamErrorState('error');
        }
      }
      return;
    }
    if (cs === 'idle') {
      streamWasLiveRef.current = false;
      autoRetryCountRef.current = 0;
      if (autoRetryTimerRef.current) { clearTimeout(autoRetryTimerRef.current); autoRetryTimerRef.current = null; }
      setStreamErrorState('idle');
      return;
    }
    if (cs === 'stopped') {
      // Clear isStreaming after a short delay so the frozen frame chip stays visible
      // long enough for the result pill to render, then resets to idle state
      setTimeout(() => setIsStreaming(false), 2000);

      // Persist stream session to DB (non-fatal)
      if (isSignedInRef.current && streamPillIdRef.current !== null && lastStreamResultRef.current !== null) {
        const pillId = streamPillIdRef.current;
        // Once-per-recording guarantee (PRI-365): this effect can re-run while
        // connectionState is still 'stopped' before refs async-clear. If a save
        // for this pill is already in flight, bail — otherwise createUserVideo /
        // saveVideoSessionEntry / trackStreamRecordingSaved would double-fire.
        if (uploadingPillIdsRef.current.has(pillId)) {
          return;
        }
        // Claim the in-flight slot synchronously (before any await/microtask) so a
        // second synchronous re-run of this effect sees it and bails at the guard
        // above. Released in the IIFE's finally block.
        uploadingPillIdsRef.current.add(pillId);
        const sessionPrompt = activePromptRef.current;
        const lastResult = lastStreamResultRef.current;
        // Snapshot history synchronously before any async work - the clear effect may
        // run before the async IIFE completes, causing an empty array to be saved.
        const historySnapshot = [...streamHistoryRef.current];
        const capturedNarrative = [...narrativeRef.current];
        const capturedElapsedS = streamSession.metering.elapsed_s;
        const capturedUserPlan = isPro ? 'pro' : 'free';
        const isYesResult = lastResult.answer?.toLowerCase().trim() === 'yes';
        const conf = lastResult.confidence ?? 0;
        const displayName = `Live: ${sessionPrompt.slice(0, 40)}${sessionPrompt.length > 40 ? '...' : ''} · ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
        void (async () => {
          try {
            const uvRes = await createUserVideo({ filename: 'live-stream', display_name: displayName });
            const uvId = uvRes.video.id;
            // Wait for MediaRecorder to finish (onstop fires async after cleanup)
            let s3Key = '';
            const blobToUpload = await streamSession.getRecordingBlob();
            if (blobToUpload && blobToUpload.size > 0) {
              try {
                // Derive extension from actual blob type
                const ext = blobToUpload.type.includes('mp4') ? 'mp4' : 'webm';
                const { s3_key } = await uploadStreamRecording({
                  filename: `stream-recording.${ext}`,
                  blob: blobToUpload,
                });
                s3Key = s3_key;
              } catch { /* non-fatal: upload failed, fall back to empty key */ }
              // Signal: a recording existed and we attempted to persist it. success
              // reflects whether the presign+upload landed an S3 key (vs. fell back
              // to empty). Lets us measure recording save-rate, not just streams watched.
              trackStreamRecordingSaved({
                success: !!s3Key,
                file_size_bytes: blobToUpload.size,
                duration_s: capturedElapsedS,
                user_plan: capturedUserPlan,
              });
            }
            await saveVideoSessionEntry(uvId, {
              prompt: sessionPrompt,
              answer: isYesResult ? 'YES' : 'NO',
              confidence: conf,
              quality: null,
              query_type: 'stream',
              result_json: { streamHistory: historySnapshot, narrative: capturedNarrative },
              video_s3_key: s3Key,
              job_id: pillId,
            });
            // Track narrative session summary analytics
            if (capturedNarrative.length > 0) {
              trackNarrativeSessionSummary({
                total_entries: capturedNarrative.length,
                session_duration_s: capturedElapsedS,
                had_idle_warning: capturedNarrative.some(e => e.type === 'status'),
                session_type: 'stream',
                user_plan: capturedUserPlan,
              });
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            setSavedUserVideos((prev: any) => [{ id: uvId, display_name: displayName } as any, ...prev]);
            // If recording was uploaded, update the current pill's recordingUrl so it
            // shows the video player (not the re-run overlay) immediately after stop.
            if (s3Key) {
              const recordingUrl = getStreamRecordingUrl(s3Key);
              setResultPills(prev => prev.map(p =>
                p.id === pillId ? { ...p, recordingUrl, userVideoId: uvId } : p
              ));
              void attachStreamRecordingUrl(pillId, recordingUrl);
            }
          } catch { /* non-fatal */ } finally {
            uploadingPillIdsRef.current.delete(pillId);
            // Clear history after save completes - not in a separate effect - to avoid
            // a React effect ordering race where the clear runs before the async save.
            streamHistoryRef.current = [];
            narrativeRef.current = [];
          }
        })();
      }

      // frozenFrameUrl is now captured inside useStreamSession.cleanup() before
      // tracks are stopped and arrives via the streamSession.frozenFrameDataUrl effect above.
    }
  }, [streamSession.connectionState]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Haptic feedback on stream lifecycle events ─────────────────────────────
  useEffect(() => {
    const cs = streamSession.connectionState;
    if (cs === 'live') vibrate(50);
    else if (cs === 'stopped') vibrate(20);
    else if (cs === 'error') vibrate([30, 20, 30]);
  }, [streamSession.connectionState]); // eslint-disable-line react-hooks/exhaustive-deps

  // streamCs is used by several effects below - declare once here
  const streamCs = streamSession.connectionState;

  // ── Offline progress tray dismissal + quota refresh on pageState change ───
  useEffect(() => {
    const prev = prevPageStateRef.current;
    if (prev === 'processing' && pageState === 'idle') {
      setOfflineProgressDismissing(true);
      const timer = setTimeout(() => setOfflineProgressDismissing(false), 600);
      prevPageStateRef.current = pageState;
      return () => clearTimeout(timer);
    }
    prevPageStateRef.current = pageState;
  }, [pageState]);

  // ── Offline pill: sync result into the pill when offline processing completes ───
  useEffect(() => {
    if (!offlineJobActive) return;
    const pillId = streamPillIdRef.current;
    if (!pillId || !pillId.startsWith('offline-')) return;

    if (pageState === 'complete' && displayedResult) {
      // Offline job finished — update the pill with the result text.
      // Do NOT set videoUrl here: /v1/video/${jobId}/video is a relative URL that
      // has no proxy on the frontend host, so the <video> element would receive
      // SPA HTML instead of the video and enter a broken state. The correct
      // absolute URL (with auth token) is set by attachRenderedVideo() which fires
      // in the onResultReceived callback a few frames later.
      setResultPills(prev => prev.map(p =>
        p.id === pillId
          ? {
              ...p,
              resultText: (displayedResult.answer as string) ?? '',
              confidence: (displayedResult.confidence as number) ?? 0,
            }
          : p
      ));
      // Reveal the slot (triggers crossfade from thumbnail to content)
      setSlotRevealed(true);
      // Keep offlineJobActive true so the unified layout stays mounted
      // (the pill with the result is inside it)
    } else if (pageState === 'idle' && offlineJobActive) {
      // Failed or cancelled — only clear if we haven't received a result
      const pill = resultPills.find(p => p.id === pillId);
      if (!pill?.resultText) {
        setOfflineJobActive(false);
      }
    }
  }, [pageState, displayedResult, offlineJobActive, jobId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Stream progress tray dismissal: show all-done briefly, then fade out ──────
  useEffect(() => {
    if (streamCs === 'buffering') {
      wentThroughBufferingRef.current = true;
      setStreamProgressDismissing(false);
    } else if (streamCs === 'live' || streamCs === 'degraded' || streamCs === 'quota_warning') {
      // Only show dismissal if we actually went through session setup (buffering)
      // Skip if transitioning back from 'recalculating' (prompt update)
      if (!wentThroughBufferingRef.current) return;
      wentThroughBufferingRef.current = false;
      setStreamProgressDismissing(true);
      const timer = setTimeout(() => setStreamProgressDismissing(false), 600);
      return () => clearTimeout(timer);
    } else if (streamCs === 'connecting' || streamCs === 'queued') {
      setStreamProgressDismissing(false);
    } else if (streamCs === 'recalculating') {
      // Never show dismissal during prompt recalculation
      wentThroughBufferingRef.current = false;
      setStreamProgressDismissing(false);
    } else {
      // idle/stopped/error - reset
      wentThroughBufferingRef.current = false;
      setStreamProgressDismissing(false);
    }
  }, [streamCs]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Recording limit banner: show once when stream goes live/buffering ────────
  // Recording limit banner: show 5 min before the limit (not at session start).
  // Only useful information when the user is actually approaching the recording cap.
  useEffect(() => {
    const elapsed = streamSession.metering.elapsed_s;
    const maxMin = streamSession.recordingMaxMinutes;
    const thresholdS = Math.max((maxMin - 5) * 60, 0); // 5 min before limit
    if (elapsed >= thresholdS && elapsed > 0 && !recordingLimitBannerShown) {
      setRecordingLimitBannerShown(true);
      const t = setTimeout(() => setRecordingLimitBannerShown(false), 10000);
      return () => clearTimeout(t);
    }
    // Reset on session end
    const cs = streamSession.connectionState;
    if (cs === 'stopped' || cs === 'idle' || cs === 'error') {
      setRecordingLimitBannerShown(false);
    }
  }, [streamSession.metering.elapsed_s, streamSession.connectionState]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Quota refresh: re-fetch after streaming or upload inference ends ─────────
  useEffect(() => {
    if (prevIsStreamingRef.current && !isStreaming) {
      setQuotaRefreshKey(k => k + 1);
    }
    prevIsStreamingRef.current = isStreaming;
  }, [isStreaming]);

  useEffect(() => {
    const prev = prevPageStateRef.current;
    if ((prev === 'processing' || prev === 'uploading') && pageState === 'idle') {
      setQuotaRefreshKey(k => k + 1);
    }
    // prevPageStateRef.current is updated by the offline-dismissal effect above
  }, [pageState]);

  // ── Bridge stream inferenceResult into result pills ─────────────────────────
  // IMPORTANT: cleanup() sets inferenceResult=null BEFORE connectionState='stopped'.
  // So we must capture the result in a ref as each frame arrives, then use that
  // ref when the session ends rather than reading from streamSession.inferenceResult.

  // Track the latest inference result in a ref as frames arrive
  useEffect(() => {
    if (streamSession.inferenceResult) {
      lastStreamResultRef.current = streamSession.inferenceResult;
    }
  }, [streamSession.inferenceResult]);

  // Push/update result pill LIVE as inference results arrive during streaming.
  // V1 showed results live in the panel - V2 does the same via a live-updating pill.
  // This also handles admin users whose sessions never auto-stop (no 60s cutoff).
  useEffect(() => {
    const result = streamSession.inferenceResult;
    if (!result || !isStreaming) return;
    if (!result.answer) return; // skip frames with no answer yet
    const isYes = result.answer.toLowerCase().trim() === 'yes';
    // Derive scalar confidence: prefer result.confidence, else max of term_confidences
    let rawConf = result.confidence;
    if (rawConf == null && result.term_confidences && typeof result.term_confidences === 'object') {
      const vals = Object.values(result.term_confidences as Record<string, number>);
      rawConf = vals.length > 0 ? Math.max(...vals) : 0;
    }
    const confidence = Math.round((rawConf ?? 0) * 100);
    const historyEntry = {
      frame_num: result.frame_num ?? 0,
      answer: result.answer ?? '',
      confidence,
      elapsed_s: result.session_fps ? Math.round((result.frame_num ?? 0) / result.session_fps) : undefined,
      term_confidences: result.term_confidences,
      recording_elapsed_ms: result.recording_elapsed_ms,  // for playback sync
    };
    if (streamPillIdRef.current) {
      // Update existing pill - keep streamHistoryRef in sync
      // No slice cap: store all frames (60s session @ ~10fps = ~600 frames, ~60KB max)
      const hist = [...streamHistoryRef.current, historyEntry];
      streamHistoryRef.current = hist;
      setResultPills(prev => prev.map(p => {
        if (p.id !== streamPillIdRef.current) return p;
        return { ...p, answer: isYes ? 'YES' : 'NO', confidence, liveData: result, streamHistory: hist,
          searchTerms: result.search_terms ?? p.searchTerms,
        };
      }));
    } else {
      // No existing pill - first result arrived but handleSubmit didn't create a pending pill
      // (can happen if stream started via a non-webcam path). Create pill now.
      const pillId = `stream-${Date.now()}`;
      streamPillIdRef.current = pillId;
      streamHistoryRef.current = [historyEntry];
      narrativeRef.current = [];
      setResultPills(prev => [...prev, {
        id: pillId,
        // Defense-in-depth: activePrompt is normally clean (set via the
        // setActivePrompt(rawPrompt) calls in useSubmitHandlers and the
        // stripping wrapper in PrimateVisionV2.tsx), but strip again here so
        // any future code path that bypasses those guards can't leak the
        // |||COMPILED_QUERY:<base64>||| envelope into pill.prompt.
        prompt: stripCompiledQueryEnvelope(activePrompt),
        answer: isYes ? 'YES' : 'NO',
        confidence,
        resultText: '',
        videoThumb: frozenFrameUrl,
        liveData: result,
        streamHistory: [historyEntry],
        narrative: [],
        searchTerms: result.search_terms ?? [],
      }]);
      // No fly animation in this fallback path - just transition immediately.
      setAfterState(true);
      setBeforeStateActive(false);
      // PRI-263 fix: do NOT clear prompt on first result - keep it active so
      // user can update the prompt mid-stream. Prompt is cleared on stream stop.
    }
  }, [streamSession.inferenceResult]); // eslint-disable-line react-hooks/exhaustive-deps

  // On session stop: clear the pill ID ref for the next session
  // NOTE: streamHistoryRef is NOT cleared here - it is cleared inside the save effect's
  // finally block (after the async save) to prevent a race where this effect runs first
  // and causes an empty history array to be persisted.
  useEffect(() => {
    const cs = streamSession.connectionState;
    if (cs === 'stopped' || cs === 'error' || cs === 'camera_denied' || cs === 'no_camera' || cs === 'queue_full') {
      // Preserve pill ID before clearing so the frozenFrame effect can update the thumb
      if (streamPillIdRef.current) lastStoppedPillIdRef.current = streamPillIdRef.current;
      streamPillIdRef.current = null;
      lastStreamResultRef.current = null;
      narrativeRef.current = [];
      // PRI-263: clear prompt textarea after stream stops
      setPrompt('');
    }
  }, [streamSession.connectionState]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── PRI-552: stamp the terminal usage block onto the stopped stream pill ───
  // terminalUsage arrives async (GET /v1/streams/{id} retry loop in
  // useStreamSession.cleanup) — by then streamPillIdRef is nulled, so target
  // the last-stopped pill. Terminal-only by construction (never set mid-session).
  useEffect(() => {
    const usage = streamSession.terminalUsage
    if (!usage || !lastStoppedPillIdRef.current) return
    const pillId = lastStoppedPillIdRef.current
    setResultPills(prev => prev.map(p => p.id === pillId ? { ...p, usage } : p))
  }, [streamSession.terminalUsage]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sync frozen webcam frame to the stopped pill's thumbnail ─────────────
  // cleanup() in useStreamSession captures a frame BEFORE stopping tracks (async).
  // By the time this resolves, streamPillIdRef is already null — use lastStoppedPillIdRef.
  useEffect(() => {
    const dataUrl = streamSession.frozenFrameDataUrl;
    if (!dataUrl) return;
    setFrozenFrameUrl(dataUrl);
    if (lastStoppedPillIdRef.current) {
      const pillId = lastStoppedPillIdRef.current;
      setResultPills(prev => prev.map(p =>
        p.id === pillId ? { ...p, videoThumb: p.videoThumb ?? dataUrl } : p
      ));
    }
  }, [streamSession.frozenFrameDataUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Inject prompt_change + context entries atomically when prompt updates ──
  useEffect(() => {
    const change = streamSession.promptChangeForNarrative;
    if (!change || !isStreaming || !streamPillIdRef.current) return;
    const { prompt: newPrompt, wallClock, elapsedS, contextEntries, bufferedEntries } = change;
    // Build entries in display order (newest-first = prepend).
    // Desired top-to-bottom:
    //   [buffered on_frame entries - newest, above the prompt-change block]
    //   Recalculating...
    //   Action X not in vocabulary...
    //   Searching for nearest match: Y
    //   Prompt changed to: {combined}
    //   [entries from before the switch]
    const promptChangeEntry = {
      wall_clock: wallClock,
      elapsed_s: elapsedS,
      window_start_elapsed_s: elapsedS,
      text: `Prompt changed to: "${newPrompt}"`,
      type: 'prompt_change' as const,
    };
    const contextNarrative = (contextEntries ?? []).map((e, i) => ({
      wall_clock: e.wall_clock,
      elapsed_s: elapsedS + 0.001 + i * 0.001,
      window_start_elapsed_s: elapsedS + 0.001 + i * 0.001,
      text: e.text,
      type: (e.entry_type ?? 'status') as NarrativeEntry['type'],
    }));
    const recalcEntry = {
      wall_clock: wallClock,
      elapsed_s: elapsedS + 0.001 + contextNarrative.length * 0.001,
      window_start_elapsed_s: elapsedS + 0.001 + contextNarrative.length * 0.001,
      text: 'Recalculating...',
      type: 'prompt_change' as const,
    };
    // promptChangeBlock: [recalc, ...context, promptChanged] (recalc = newest in this block)
    const promptChangeBlock = [recalcEntry, ...contextNarrative, promptChangeEntry];
    // buffered on_frame entries appear ABOVE the prompt-change block (they're newer - happened after recalc)
    const buffered = bufferedEntries ?? [];
    const updated = [...buffered, ...promptChangeBlock, ...narrativeRef.current];
    narrativeRef.current = updated;
    const pillId = streamPillIdRef.current;
    setResultPills(prev => prev.map(p => p.id === pillId ? { ...p, narrative: updated } : p));
  }, [streamSession.promptChangeForNarrative]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Update live pill header when server combines a fragment prompt ─────────
  // Defense-in-depth: useStreamSession already strips the |||COMPILED_QUERY:<base64>|||
  // envelope before setting combinedPrompt, but we strip again here so the pill state
  // can never hold the wire format even if a future code path bypasses the upstream
  // strip. V2ResultPill also strips at render time — three layers, no leaks.
  useEffect(() => {
    const combined = streamSession.combinedPrompt;
    if (!combined || !streamPillIdRef.current || !isStreaming) return;
    const cleaned = stripCompiledQueryEnvelope(combined);
    const pillId = streamPillIdRef.current;
    setResultPills(prev => prev.map(p => p.id === pillId ? { ...p, prompt: cleaned } : p));
  }, [streamSession.combinedPrompt]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Accumulate narrative entries from streaming ──────────────────────────────
  useEffect(() => {
    const entry = streamSession.narrativeEntry;
    if (!entry || !isStreaming || !streamPillIdRef.current) return;
    // Prepend to keep newest-first order
    const updated = [entry, ...narrativeRef.current];
    narrativeRef.current = updated;
    const pillId = streamPillIdRef.current;
    setResultPills(prev => prev.map(p => {
      if (p.id !== pillId) return p;
      return { ...p, narrative: updated };
    }));
    // Track analytics
    void Promise.resolve().then(() => {
      trackNarrativeGenerated({
        entry_type: entry.type,
        session_type: 'stream',
        prompt_query_type: streamSession.inferenceResult?.query_type ?? 'unknown',
        user_plan: isPro ? 'pro' : 'free',
      });
      // Server-side narrative fallback (LLM failure / budget cap / grounding
      // fail) is surfaced via entry.fallback_reason — wire it to the tracker.
      if (entry.fallback_reason) {
        trackNarrativeFallback({
          reason: entry.fallback_reason,
          session_type: 'stream',
        });
      }
    });
  }, [streamSession.narrativeEntry]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handle narrative_summary at session end ────────────────────────────────
  useEffect(() => {
    const summary = streamSession.narrativeSummary;
    if (!summary || !summary.length) return;
    // Find the most recently stopped stream pill and apply the canonical narrative
    // (narrativeSummary arrives just before/during session end)
    setResultPills(prev => {
      // Apply to the last stream pill that has no final narrative yet, or has fewer entries
      const targetId = prev.filter(p => p.id.startsWith('stream-')).at(-1)?.id;
      if (!targetId) return prev;
      return prev.map(p => {
        if (p.id !== targetId) return p;
        return { ...p, narrative: summary };
      });
    });
  }, [streamSession.narrativeSummary]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Accumulate result pills when a new displayedJobId arrives ─────────────
  useEffect(() => {
    if (!displayedJobId || !displayedResult) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dr = displayedResult as Record<string, any>;
    const isYes = (dr.answer as string)?.toLowerCase().trim() === 'yes';
    const confidence = Math.round(((dr.confidence as number) ?? 0) * 100);
    // Defense-in-depth: strip the |||COMPILED_QUERY:<base64>||| envelope
    // from both activePrompt and dr.query. The upstream setActivePrompt and
    // server-side query fields should already be clean, but a single layer
    // of stripping here means pill.prompt can never hold the wire format
    // even if a future code path or persisted DB row leaks one in.
    const pillPrompt = stripCompiledQueryEnvelope(activePrompt || (dr.query as string) || '');
    const pillQueryType = (dr.query_type as string) ?? undefined;
    const capturedJobId = displayedJobId;

    let wasOfflineMerge = false;
    setResultPills(prev => {
      if (prev.some(p => p.id === capturedJobId)) return prev; // deduplicate
      // Offline path creates an 'offline-' placeholder pill via startFlyAnimation.
      // Merge the server result into that pill (rename its id to the server job id)
      // instead of appending a duplicate.
      const offlineIdx = prev.findIndex(p => p.id.startsWith('offline-'));
      if (offlineIdx !== -1) {
        wasOfflineMerge = true;
        return prev.map((p, i) => i !== offlineIdx ? p : {
          ...p,
          id: capturedJobId,
          answer: isYes ? 'YES' : 'NO',
          confidence,
          query_type: pillQueryType,
          resultPayload: { ...dr } as Record<string, unknown>,
          // PRI-552: terminal settlement summary from GET /v1/analyses/{id}.
          usage: (dr.usage as ResultPill['usage']) ?? null,
          videoUrl: null,
          searchTerms: (dr.search_terms as string[]) ?? [],
        });
      }
      const newPill: ResultPill = {
        id: capturedJobId,
        prompt: pillPrompt,
        answer: isYes ? 'YES' : 'NO',
        confidence,
        resultText: '',
        videoThumb: submittedThumbRef.current,
        query_type: pillQueryType,
        resultPayload: { ...dr } as Record<string, unknown>,
        // PRI-552: terminal settlement summary from GET /v1/analyses/{id}.
        usage: (dr.usage as ResultPill['usage']) ?? null,
        // videoUrl will be updated async once we have the auth token for the rendered video
        videoUrl: null,
        narrative: [],
        searchTerms: (dr.search_terms as string[]) ?? [],
      };
      return [...prev, newPill];
    });
    // If we merged into an offline placeholder, update the ref so downstream effects
    // (offline sync, narrative, frozen-frame) track the correct server job id.
    if (wasOfflineMerge) {
      streamPillIdRef.current = capturedJobId;
      setSlotRevealed(true);
    }
    // Clear the prompt now that the result pill is visible
    setPrompt('');

    // Fetch the rendered output video from the inference server.
    // The /v1/video/{jobId}/video endpoint requires auth - pass token as query param
    // since HTML <video> elements cannot set Authorization headers.
    void attachRenderedVideo(capturedJobId);

    // Save to session (non-fatal; skip stream query types)
    if (isSignedIn && (displayedResult.query_type as string) !== 'stream') {
      const activeUserVideoId = selectedSavedVideo?.id ?? null;
      const activeDemoVideoId = selectedDemoVideo;
      const capturedDemoMap = demoVideoUserVideoIds;
      const capturedDemoVideos = demoVideos;
      // For newly uploaded files, selectedSavedVideo is null but the videoQueue
      // entry has userVideoId once createUserVideo resolves in useAnalysis.
      const uploadedUserVideoId = videoQueueRef.current.find(v => v.id === activeVideoId)?.userVideoId ?? null;

      const doSave = async () => {
        try {
          let videoId: string | null = null;

          if (activeUserVideoId) {
            videoId = activeUserVideoId;
          } else if (uploadedUserVideoId) {
            videoId = uploadedUserVideoId;
          } else if (activeDemoVideoId) {
            let uvId = capturedDemoMap[activeDemoVideoId];
            if (!uvId) {
              const dv = capturedDemoVideos.find(v => v.id === activeDemoVideoId);
              const res = await createUserVideo({
                filename: dv?.display_name ?? activeDemoVideoId,
                display_name: dv?.display_name ?? activeDemoVideoId,
              });
              uvId = res.video.id;
              setDemoVideoUserVideoIds(prev => ({ ...prev, [activeDemoVideoId]: uvId }));
            }
            videoId = uvId;
          }

          if (!videoId) return;

          const res = await saveVideoSessionEntry(videoId, {
            prompt: pillPrompt,
            answer: isYes ? 'YES' : 'NO',
            confidence,
            quality: null,
            query_type: (pillQueryType as string) ?? null,
            result_json: { ...dr } as Record<string, unknown>,
            video_s3_key: '',
            job_id: displayedJobId,
          });

          setResultPills(prev => prev.map(p =>
            p.id === displayedJobId ? { ...p, sessionResultId: res.id, savedAt: res.created_at } : p
          ));
        } catch { /* non-fatal */ }
      };
      void doSave();
    }
  }, [displayedJobId]); // eslint-disable-line react-hooks/exhaustive-deps


}
